import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { NavOverflowItem } from './NavOverflow';

/**
 * Phone / tablet navigation for the register header.
 *
 * Below the ``lg`` breakpoint the inline link row (up to nine links
 * for an operator, plus the account cluster) cannot fit, so the header
 * collapses it into a single menu button. Tapping it drops a full-
 * width panel under the header listing every nav item as a 48px tap
 * row, followed by the account block (role pill, e-mail, sign-out).
 *
 * Behaviour:
 *  * Closes on route change, on Escape, and on backdrop tap.
 *  * Locks page scroll while open so a long operator menu scrolls
 *    inside the panel rather than dragging the page behind it.
 *  * The trigger carries ``aria-expanded`` + ``aria-controls`` and
 *    flips its label between "Menu" and "Close menu".
 *
 * Rendered only for authenticated users: anonymous visitors have no
 * nav items, so the header keeps the plain sign-in CTA inline instead.
 */
interface MobileNavProps {
  items: NavOverflowItem[];
  /** Single active nav path resolved by the header (longest match). */
  activeTo: string | null;
  roleLabel: string | null;
  /**
   * The two-hat switch for an operator with a home lender, rendered in
   * the account block in place of the role pill. `null` for everyone
   * else, who keep the pill.
   */
  hatControl?: ReactNode;
  email: string | null;
  onLogout: () => void;
  /**
   * Text of the sign-out button. Defaults to "Log ud"; the header passes
   * "Afslut" while the admin is logged in as another user, because
   * there the button ends that session instead of signing out.
   */
  logoutLabel?: string;
}

const PANEL_ID = 'lexta-mobile-nav';

export function MobileNav({
  items,
  activeTo,
  roleLabel,
  hatControl = null,
  email,
  onLogout,
  logoutLabel,
}: MobileNavProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Escape closes; page scroll is locked while the panel is open.
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const badgeTotal = items.reduce((sum, i) => sum + (i.badge ?? 0), 0);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={PANEL_ID}
        aria-label={open ? t('header.closeMenu') : t('header.menu')}
        className="relative inline-flex h-11 w-10 items-center justify-end text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] transition-colors"
      >
        {open ? (
          <X className="w-5 h-5" strokeWidth={1.5} />
        ) : (
          <Menu className="w-5 h-5" strokeWidth={1.5} />
        )}
        {!open && badgeTotal > 0 && (
          <span
            aria-label={t('header.offersBadge', { count: badgeTotal })}
            className="absolute top-1 right-0 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[var(--color-alert)] text-white text-[9px] font-mono tabular"
          >
            {badgeTotal}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop — dims the page and closes the menu on tap. */}
            <motion.div
              key="backdrop"
              aria-hidden
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-10"
              style={{ background: 'oklch(0.18 0.025 250 / 0.35)' }}
            />
            <motion.div
              key="panel"
              id={PANEL_ID}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
              className="absolute inset-x-0 top-full z-30 bg-white border-b border-[var(--color-rule)] max-h-[calc(100dvh-5rem)] overflow-y-auto"
            >
              <nav aria-label={t('header.menu')}>
                {items.map((item) => {
                  const active = item.to === activeTo;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center justify-between gap-3 min-h-12 px-[var(--page-gutter)] py-3 text-[15px] border-b border-[var(--color-rule-soft)] border-l-[3px] transition-colors ${
                        active
                          ? 'border-l-[var(--color-primary)] bg-[var(--color-primary-soft)] text-[var(--color-ink)] font-medium'
                          : 'border-l-transparent text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] hover:bg-[var(--color-paper-deep)]'
                      }`}
                    >
                      <span>{item.label}</span>
                      {item.badge ? (
                        <span
                          aria-label={t('header.offersBadge', { count: item.badge })}
                          className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--color-alert)] text-white text-[10px] font-mono tabular"
                        >
                          {item.badge}
                        </span>
                      ) : null}
                    </Link>
                  );
                })}
              </nav>

              {/* Account block — mirrors the desktop header's role pill +
                  e-mail + sign-out cluster. */}
              <div className="px-[var(--page-gutter)] py-4 bg-[var(--color-paper-deep)] flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="section-label mb-1.5">{t('header.account')}</div>
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    {hatControl ? (
                      <div className="shrink-0">{hatControl}</div>
                    ) : (
                      roleLabel && (
                        <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-primary-strong)] px-1.5 py-0.5 bg-[var(--color-primary-soft)]">
                          {roleLabel}
                        </span>
                      )
                    )}
                    {email && (
                      <span className="font-mono text-xs text-[var(--color-ink-muted)] tabular truncate">
                        {email}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onLogout}
                  className="shrink-0 min-h-11 px-4 py-2 text-xs font-mono uppercase tracking-wider border border-[var(--color-rule)] text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] hover:border-[var(--color-ink)] transition-colors"
                >
                  {logoutLabel ?? t('auth.signOut')}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
